import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faBug, faCircleCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '../contexts/UserContext';

// Bump this version to re-surface the notice to everyone (e.g. when the message changes).
const NOTICE_VERSION = 'sso-v1';
const dismissKey = (sub) => `platformTransitionNotice_${NOTICE_VERSION}_${sub || 'anon'}`;

const PlatformTransitionNotice = () => {
  const navigate = useNavigate();
  const { user: authUser, authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { termsAccepted } = useUser();

  const [show, setShow] = useState(false);

  const sub = authUser?.attributes?.sub;

  useEffect(() => {
    // Only surface for authenticated users on the dashboard, and never on top of the Terms gate.
    if (authStatus !== 'authenticated' || !sub || !termsAccepted) return;
    if (localStorage.getItem(dismissKey(sub))) return;

    let poll;

    // Bootstrap marks <body> with `modal-open` while any modal is shown. Give any auto-opening
    // modal (Terms, Award, Tutorial, ...) time to mount first, then poll until the page is clear
    // of other modals so this notice always appears last.
    const startDelay = setTimeout(() => {
      poll = setInterval(() => {
        if (document.body.classList.contains('modal-open')) return; // another modal is open — wait
        setShow(true);
        clearInterval(poll);
      }, 400);
    }, 1000);

    return () => {
      clearTimeout(startDelay);
      if (poll) clearInterval(poll);
    };
  }, [authStatus, sub, termsAccepted]);

  // Shown ONCE per user (until NOTICE_VERSION is bumped): any close marks it seen.
  const closeNotice = () => {
    if (sub) localStorage.setItem(dismissKey(sub), 'true');
    setShow(false);
  };

  const goToReportIssues = () => {
    closeNotice();
    navigate('/issue-reporting');
  };

  return (
    <Modal show={show} onHide={closeNotice} centered size="lg" scrollable>
      <div
        className="d-flex align-items-center gap-3 px-4 py-3 text-white"
        style={{
          background: 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
          }}
        >
          <FontAwesomeIcon icon={faBullhorn} size="lg" />
        </div>
        <div>
          <h5 className="mb-0 fw-semibold">Your sign-in has been upgraded</h5>
          <small style={{ opacity: 0.85 }}>One secure account, seamless access across all our platforms</small>
        </div>
      </div>

      <Modal.Body className="px-4 py-4">
        <p>
          We&rsquo;ve moved <strong>LeanFITT&trade; Pro</strong> from the old, standalone login to a single,
          secure <strong>single sign-on (SSO)</strong> powered by the <strong>FITTWorks hub</strong>. From now
          on you sign in once and move seamlessly across all of our platforms &mdash; with no separate logins to
          remember.
        </p>

        {/* Benefit — green */}
        <div className="rounded p-3 my-3" style={{ backgroundColor: '#e8f5e9', borderLeft: '4px solid #2e7d32' }}>
          <div className="d-flex align-items-start gap-2">
            <FontAwesomeIcon icon={faCircleCheck} className="mt-1 flex-shrink-0" style={{ color: '#2e7d32' }} />
            <div>
              <p className="mb-1 fw-semibold" style={{ color: '#1b5e20' }}>One account for everything</p>
              <p className="mb-0 small text-muted">
                Your single FITTWorks account now gives you access to LeanFITT&trade; Pro, LeadershipFITT&trade;,
                and everything we build next.
              </p>
            </div>
          </div>
        </div>

        {/* Impact — amber */}
        <div className="rounded p-3 my-3" style={{ backgroundColor: '#fff8e1', borderLeft: '4px solid #f9a825' }}>
          <div className="d-flex align-items-start gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 flex-shrink-0" style={{ color: '#f9a825' }} />
            <div>
              <p className="mb-1 fw-semibold" style={{ color: '#8d6e00' }}>Some records may have been affected</p>
              <p className="mb-1 small text-muted">
                Migrating every account is a large change, so a small number of records may not yet be linked to
                your organization, or may appear out of place. Your data is safe &mdash; nothing has been deleted.
              </p>
              <p className="mb-0 small text-muted">
                Where we detect this you&rsquo;ll see a small{' '}
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f9a825' }} /> marker &mdash; tap it
                to see why the item is showing and assign it to the right organization.
              </p>
            </div>
          </div>
        </div>

        {/* Report — brand blue */}
        <div
          className="rounded p-3 my-3"
          style={{ backgroundColor: 'var(--primary-transparent)', borderLeft: '4px solid var(--primary)' }}
        >
          <div className="d-flex align-items-start gap-2">
            <FontAwesomeIcon icon={faBug} className="mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} />
            <div>
              <p className="mb-1">
                If anything looks off &mdash; missing information, records under the wrong organization, or any
                unexpected behavior &mdash; please report it through:
              </p>
              <p className="mb-2 fw-semibold" style={{ color: 'var(--primary-dark)' }}>
                Main Menu → User Tools → Report Issues (Bugs)
              </p>
              <p className="mb-0 small text-muted">
                Every report helps us track, prioritize, and resolve issues faster while we finish hardening the
                new system.
              </p>
            </div>
          </div>
        </div>

        <p className="mb-0 text-muted">
          Thank you for your patience, and for helping us make the platform better.
        </p>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end gap-2 px-4">
        <Button variant="outline-primary" onClick={goToReportIssues}>
          <FontAwesomeIcon icon={faBug} className="me-2" />
          Report an Issue
        </Button>
        <Button variant="primary" onClick={closeNotice}>
          <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlatformTransitionNotice;
